import React from 'react';
import { useTranslation } from 'react-i18next';
import AddIcon from '@material-ui/icons/Add';
import { FieldArray, FormikProps } from 'formik';
import { Box, Button } from '@material-ui/core';
import RecipeLineWrapper from '../RecipeLineWrapper';
import { generateTranslationByVenueType } from '../../../../utils';
import { useUser } from '../../../../utils/hooks/auth';
import { ProductFormValues } from './types';
import RecipeLineField from './RecipeLineField';
import { EMPTY_RECIPE_LINE } from './constants';

interface RecipeBuilderProps {
    name: string;
    warehouseIds: string[];
    removeRecipe: () => void;
}

const STOCK_CARD_ID_NAME = 'stockCardId';
const STOCK_CARD_UNIT_NAME = 'unitType';
const STOCK_CARD_QUANTITY = 'quantity';
const STOCK_CARD_WAREHOUSE_NAME = 'warehouseId';
const STOCK_CARD_ABSOLUTE_WASTE = 'absoluteWaste';
const STOCK_CARD_WASTE_PERCENTAGE = 'wastePercentage';
const RECIPE_LINE_STOCK_CARD = 'stockCard';

const RecipeBuilder = ({ name, warehouseIds, removeRecipe }: RecipeBuilderProps ) => {
    const { t } = useTranslation();
    const { user: { venueType } } = useUser();

    return (
        <FieldArray
            name={name}
            render={
                ({ remove, push, form }) => {
                    const addRecipeLine = () => push(EMPTY_RECIPE_LINE);
                    const { values: { recipeLines } } = form as FormikProps<ProductFormValues>;
                    const recipeLinesChildren = recipeLines?.map((recipeLine, index) => {
                        const removeRecipeLine = () => {
                            remove(index);
                            if (recipeLines?.length === 1) {
                                removeRecipe();
                            }
                        };

                        return (
                            <RecipeLineWrapper key={`recipeLines.${index}.name`} onRemove={removeRecipeLine} recipeLineIndex={index}>
                                <RecipeLineField
                                    avaialbleWarehouseIds={warehouseIds}
                                    absoluteWasteFieldProps={{
                                            formikProps: form,
                                            name: `recipeLines.${index}.${STOCK_CARD_ABSOLUTE_WASTE}`,
                                            label: t('Waste quantity'),
                                        }}
                                    relativeWasteFieldProps={{
                                            formikProps: form,
                                            name: `recipeLines.${index}.${STOCK_CARD_WASTE_PERCENTAGE}`,
                                            label: t('Waste %'),
                                        }}
                                    quantityFieldProps={{
                                        formikProps: form,
                                        name: `recipeLines.${index}.${STOCK_CARD_QUANTITY}`,
                                        label: t('Quantity'),
                                    }}
                                    unitTypeFieldProps={{
                                        name: `recipeLines.${index}.${STOCK_CARD_UNIT_NAME}`,
                                        variant: 'outlined',
                                        formikProps: form,
                                        label: t('Unit type'),
                                    }}
                                    searchFieldProps={{
                                        name: `recipeLines.${index}.${STOCK_CARD_ID_NAME}`,
                                        label: t('Warehouse card'),
                                        currentWarehouseCard: recipeLine.stockCard,
                                        stockCardFieldName: `recipeLines.${index}.${RECIPE_LINE_STOCK_CARD}`,
                                        warehouseIds,
                                    }}
                                    warehouseFieldProps={{
                                        name: `recipeLines.${index}.${STOCK_CARD_WAREHOUSE_NAME}`,
                                        variant: 'outlined',
                                        label: t('Warehouse'),
                                        formikProps: form
                                    }}
                                />
                            </RecipeLineWrapper>
                    );
                });

                return (
                    <Box display="flex" flexDirection="column">
                        {recipeLinesChildren}
                        <Box mt={2} />
                        <Box>
                            <Button
                                color='primary'
                                variant='outlined'
                                startIcon={<AddIcon />}
                                onClick={addRecipeLine}
                            >
                                {generateTranslationByVenueType({
                                    translations: {
                                        Ecommerce:  t('Composite line'),
                                        Restaurant: t('Recipe line'),
                                    },
                                    venueType,
                                })}
                            </Button>
                        </Box>
                    </Box>
                );
            }
}
        />
    );
};

export default RecipeBuilder;
